import { motion, useReducedMotion } from 'motion/react';

import { cn } from '@/lib/utils';

import { ARTIFACT_SKELETON_CLASS } from './artifact.constants';
import { ArtifactArchiveGlyph } from './ArtifactArchiveGlyph';

import type { ReactNode } from 'react';

export type ArtifactPaperSkeleton =
  | 'document'
  | 'text'
  | 'spreadsheet'
  | 'archive';

interface ArtifactPaperProps {
  children?: ReactNode;
  isLoading?: boolean;
  skeleton?: ArtifactPaperSkeleton;
}

const DOCUMENT_LINE_WIDTHS = ['w-full', 'w-4/5', 'w-full', 'w-2/3'];

const TEXT_LINES = [
  'h-2 w-3/5',
  'h-1 w-full',
  'h-1 w-full',
  'h-1 w-4/5',
  'h-1.5 w-2/5',
  'h-1 w-full',
  'h-1 w-3/4',
  'h-1 w-full',
  'h-1 w-1/2',
];

const SPREADSHEET_COLUMNS_CLASS = 'grid-cols-[1.7fr_1fr_0.9fr_1.2fr]';

const SPREADSHEET_CELLS: (string | null)[][] = [
  ['w-4/5', 'w-full', 'w-3/4', 'w-2/3'],
  ['w-full', 'w-2/3', 'w-full', 'w-1/2'],
  ['w-3/4', 'w-full', 'w-1/2', 'w-4/5'],
  ['w-full', 'w-1/2', null, 'w-full'],
  ['w-2/3', 'w-full', 'w-4/5', 'w-2/3'],
  ['w-full', 'w-3/4', 'w-1/2', 'w-full'],
  ['w-1/2', 'w-full', 'w-4/5', null],
  ['w-full', 'w-2/3', 'w-full', 'w-1/2'],
  ['w-3/4', 'w-1/2', 'w-2/3', 'w-full'],
  ['w-full', 'w-full', 'w-1/2', 'w-3/4'],
  ['w-2/3', 'w-3/4', 'w-full', 'w-1/2'],
];

const PAPER_SHIMMER_CLASSES = cn(
  'pointer-events-none absolute inset-0',
  'bg-[linear-gradient(to_right,transparent_0%,transparent_35%,var(--surface-hover)_50%,transparent_65%,transparent_100%)]',
  'bg-[length:200%_100%] [background-position:0%_0] opacity-70',
  'animate-shimmer-rename-field'
);

export const PaperSkeleton = ({
  variant,
}: {
  variant: ArtifactPaperSkeleton;
}) => {
  const bar = (size: string) =>
    cn(ARTIFACT_SKELETON_CLASS, size, 'animate-none');

  if (variant === 'archive') {
    return (
      <div className="flex size-full items-start justify-center pt-5 text-grayA-3">
        <ArtifactArchiveGlyph className="w-[64px]" />
      </div>
    );
  }

  if (variant === 'spreadsheet') {
    return (
      <div className="flex size-full flex-col gap-1 p-2.5">
        {SPREADSHEET_CELLS.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className={cn('grid items-center gap-1', SPREADSHEET_COLUMNS_CLASS)}
          >
            {row.map((width, columnIndex) =>
              width ? (
                <div
                  key={columnIndex}
                  className={bar(cn(rowIndex === 0 ? 'h-2' : 'h-1.5', width))}
                />
              ) : (
                <div key={columnIndex} />
              )
            )}
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'text') {
    return (
      <div className="flex size-full flex-col gap-1.5 p-2.5">
        {TEXT_LINES.map((line, index) => (
          <div key={index} className={bar(line)} />
        ))}
      </div>
    );
  }

  return (
    <div className="flex size-full flex-col gap-1.5 p-2.5">
      <div className={bar('h-8 w-full')} />
      {DOCUMENT_LINE_WIDTHS.map((width, index) => (
        <div key={index} className={bar(cn('h-1.5', width))} />
      ))}
    </div>
  );
};

export const ArtifactPaper = ({
  children,
  isLoading = true,
  skeleton = 'document',
}: ArtifactPaperProps) => {
  const shouldReduceMotion = useReducedMotion() ?? false;

  return (
    <div
      aria-hidden
      inert
      className="pointer-events-none relative h-full w-36 shrink-0"
    >
      <div className="absolute -inset-x-10 -top-16 bottom-0 overflow-hidden">
        <div
          className={cn(
            'absolute left-[38%] top-[52px] h-[150px] -translate-x-1/2 rotate-[5deg]',
            skeleton === 'archive' ? 'w-[104px]' : 'w-[120px]',
            'overflow-hidden rounded-md border border-grayA-3 bg-surface shadow-md',
            !shouldReduceMotion &&
              'transition-transform duration-200 [@media(hover:hover)_and_(pointer:fine)]:group-hover:-translate-y-2 [@media(hover:hover)_and_(pointer:fine)]:group-hover:rotate-[3deg]'
          )}
        >
          {children ? (
            <motion.div
              initial={shouldReduceMotion ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                duration: shouldReduceMotion ? 0 : 0.35,
                ease: 'easeOut',
              }}
              className="size-full"
            >
              {children}
            </motion.div>
          ) : (
            <>
              <PaperSkeleton variant={skeleton} />
              {isLoading && !shouldReduceMotion && (
                <div aria-hidden className={PAPER_SHIMMER_CLASSES} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
